import {
    ButtonHeading, CommonDateRange, CustomPagination,
    CustomToolTip, CustomSelect, CityDropdownForTrip,
    CommonInput
} from 'components';
import BootstrapTable from 'components/BootstrapTable';
import Tab from 'components/common/Tab';
import { constant } from 'constants/constant';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPaymentTransactionListApi, getAllVehicleNumber, venderDropdownApi } from '_services';
import StorageService from 'utils/StorageServices';
import { UserContext } from 'context';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { WorkshopServicingVoucher } from 'pages/WorkShop/WorkshopServicing';
import SingleSelectDropdownInfinite from 'components/common/SingleSelectDropdownInfinite/SingleSelectDropdownInfinite';
import toast from 'react-hot-toast';
import { GenExpListTab } from '.';
import moment from 'moment';
import { AddExpensesModal } from 'modals';
import { getAllPermissions } from 'utils/permissionHelper';

const EXTENDED_FILTER_TABS = [2];

const paymentStatusOptions = [
    { label: "Pending", value: "Pending" },
    { label: "Completed", value: "Completed" },
    { label: "Partially Paid", value: "Partially Paid" },
];

// ─── Unified Filter Bar ───────────────────────────────────────────────────────
const TransactionFilterBar = ({
    dateRange, onDateChange, dateResetKey,
    search, onSearchChange,
    showExtended,
    vehicleId, onVehicleChange,
    vendorId, onVendorChange,
    status, onStatusChange,
    onReset, isDisabled,
    fetchVehicle, fetchVendor,
}) => (
    <div className="trs-exp-head">
        <div className="trs-filter-item-date">
            {/* ✅ key prop: reset click pe CommonDateRange remount होगा — internal state clear */}
            <CommonDateRange
                key={dateResetKey}
                onApply={onDateChange}
                initialStartDate={dateRange.startDate || ""}
                initialEndDate={dateRange.endDate || ""}
                datePrefield={!!(dateRange?.startDate && dateRange?.endDate)}
            />
        </div>

        <div className="trs-filter-item">
            <CommonInput
                type="text"
                placeholder="Search"
                className="simpleFilter_input"
                name="search"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
            />
        </div>

        {showExtended && (
            <>
                <div className="trs-filter-item">
                    <SingleSelectDropdownInfinite
                        placeholder="Vehicle Number"
                        fetchData={fetchVehicle}
                        selectedItem={vehicleId}
                        onChange={onVehicleChange}
                        loadOnOpen={true}
                        vehicleStatus={false}
                    />
                </div>

                <div className="trs-filter-item">
                    <CityDropdownForTrip
                        placeholder="Select Vendor"
                        fetchData={fetchVendor}
                        pageSize={20}
                        onChange={onVendorChange}
                        selectedItem={vendorId || null}
                    />
                </div>

                <div className="trs-filter-item">
                    <CustomSelect
                        options={paymentStatusOptions}
                        name="bill_status"
                        placeholder="Bill Status"
                        onSelect={(data) => onStatusChange(data?.value)}
                        value={
                            status
                                ? paymentStatusOptions.find(i => i.value === status)
                                : { label: "", value: "" }
                        }
                    />
                </div>
            </>
        )}

        {/* ✅ Reset button — sabhi tabs pe common */}
        <button
            className="anime-btn __reSet_btn_g trs-filter-item-g"
            disabled={isDisabled}
            onClick={onReset}
        >
            Reset
        </button>
    </div>
);

const PlaceholderTab = ({ label }) => <div className="trs-placeholder-tab">{label}</div>;

// ─── Main Component ───────────────────────────────────────────────────────────
const PaymentTransaction = () => {
    useDocumentTitle("Vouchers");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { allMenu } = useContext(UserContext)
    // ✅ FIX: URL param ko Number me parse karo — string "2" !== number 2
    const tabFromUrl = Number(searchParams.get('tab')) || 1;

    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const [offset, setOffset] = useState(1);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [selectedRange, setSelectedRange] = useState({ startDate: "", endDate: "" });

    // Extended filter state (tab 2 only)
    const [vehicleId, setVehicleId] = useState({});
    const [vendorId, setVendorId] = useState({});
    const [status, setStatus] = useState("");
    const [openExpenseModal, setOpenExpenseModal] = useState(false);
    const [dateResetKey, setDateResetKey] = useState(0); // ✅ force remount key
    const hasLinkPermission = getAllPermissions("vouchers", allMenu, 'label');
    const hasLinkServicePermission = getAllPermissions("Servicing", allMenu, 'label');


    const itemsPerPage = 10;

    // ✅ FIX: Number me compare — EXTENDED_FILTER_TABS = [2] (numbers)
    const showExtendedFilter = EXTENDED_FILTER_TABS.includes(activeTab);

    // ✅ FIX: URL param change hone par activeTab Number me set karo
    useEffect(() => {
        setActiveTab(tabFromUrl);
    }, [tabFromUrl]);

    // Reset everything on tab switch
    useEffect(() => {
        setOffset(1);
        setVehicleId({});
        setVendorId({});
        setStatus("");
    }, [activeTab]);

    // Reset offset on any filter change
    useEffect(() => { setOffset(1); }, [search, dateRange, vehicleId, vendorId, status]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const handleApply = (newRange) => {
        setSelectedRange(newRange);
        setDateRange((prev) => ({
            ...prev,
            start_date: moment(newRange.startDate).format("YYYY-MM-DD"),
            end_date: moment(newRange.endDate).format("YYYY-MM-DD"),
        }));
    };

    const fetchVehicle = async (search, page, limit) => {
        const res = await getAllVehicleNumber({
            company_id: StorageService?.getCompanyId(),
            offset: page, limit, search,
            vehicle_ownership: [1, 3],
        });
        if (res?.responseCode === 200) {
            return {
                items: res?.data?.vehicle_list?.map(i => ({ ...i, name: i?.vehicle_no })),
                total: res?.data?.total_record,
            };
        }
        return { items: [], total: 0 };
    };

    const fetchVendor = useCallback(async (search, page, limit) => {
        try {
            const res = await venderDropdownApi({
                company_id: StorageService.getCompanyId(),
                offset: page, limit, search,
            });
            if (res?.responseCode === 200) {
                return {
                    items: res.data.vendor_list.map(v => ({ ...v, label: v.name, value: v._id })),
                    total: res.data.total_record,
                };
            }
            toast.error(res?.responseMessage);
        } catch (e) { console.error(e); }
        return { items: [], total: 0 };
    }, []);

    const isResetDisabled =
        !search && !vehicleId?._id && !vendorId?.value && !status && !dateRange;

    const handleReset = () => {
        setSearch("");
        setDebouncedSearch("");
        setDateRange(null);
        setSelectedRange({ startDate: "", endDate: "" }); // ✅ date display reset
        setDateResetKey(k => k + 1);                      // ✅ CommonDateRange remount
        setVehicleId({});
        setVendorId({});
        setStatus("");
    };

    const tabProps = {
        tabList: constant.accountTransactionTabs,
        // ✅ Tab click pe bhi Number set hoga
        callBackTab: (t) => setActiveTab(Number(t)),
        activeTab,
    };

    const renderTabContent = () => {
        // ✅ FIX: activeTab ab guaranteed Number hai — switch cases match honge
        switch (activeTab) {
            case 1: return (
                <GenExpListTab
                    itemsPerPage={itemsPerPage}
                    offset={offset}
                    onPageChange={setOffset}
                    search={debouncedSearch}
                    dateRange={dateRange}
                    openExpenseModal={openExpenseModal}
                    onExpenseModalClose={() => setOpenExpenseModal(false)}
                    activeTab={activeTab}
                />
            );
            case 2: return (
                <WorkshopServicingVoucher
                    search={debouncedSearch}
                    dateRange={dateRange}
                    vehicleId={vehicleId}
                    vendorId={vendorId}
                    status={status}
                    offset={offset}
                    onPageChange={setOffset}
                    activeTab={activeTab}
                />
            );
            case 3: return <PlaceholderTab label="EMI" />;
            case 4: return <PlaceholderTab label="Documents" />;
            case 5: return <PlaceholderTab label="Salary" />;
            case 6: return <PlaceholderTab label="Purchase" />;
            case 7: return <PlaceholderTab label="Pay Claim" />;
            case 8: return <PlaceholderTab label="Charge Claim" />;
            default: return null;
        }
    };

    const getButtonTitle = () => {
    if (activeTab === 1) {
        return hasLinkPermission?.canCreate ? "Add Expense" : "";
    }
    if (activeTab === 2) {
        return hasLinkServicePermission?.canCreate ? "Add Service" : "";
    }
    return "";
};

    return (
        <div className='transation-all-mainBx'>
            <ButtonHeading
                title="Transactions"
               buttonTitle={getButtonTitle()}
                onClick={() => {
                    if (activeTab === 1) {
                        setOpenExpenseModal(true);
                    } else {
                        navigate('/WorkshopServicing/AddService');
                    }
                }}
                className="transactions-head-button"
            />
            <div className="addPr_flBx __uiTx_new">
                <Tab {...tabProps} />
                <div className='transation-subContents'>
                    <div className='transaction-exp-bxs'>
                        <TransactionFilterBar
                            dateRange={selectedRange} onDateChange={handleApply}
                            dateResetKey={dateResetKey}
                            search={search} onSearchChange={setSearch}
                            showExtended={showExtendedFilter}
                            vehicleId={vehicleId} onVehicleChange={setVehicleId}
                            vendorId={vendorId} onVendorChange={setVendorId}
                            status={status} onStatusChange={setStatus}
                            onReset={handleReset}
                            isDisabled={isResetDisabled}
                            fetchVehicle={fetchVehicle}
                            fetchVendor={fetchVendor}
                        />

                        {renderTabContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentTransaction;